/**
 * Flexible date strip for live Results — dates only, never invent prices.
 */

const WEEKDAYS_HE = [
  "ראשון",
  "שני",
  "שלישי",
  "רביעי",
  "חמישי",
  "שישי",
  "שבת",
];

/** Visible cells are this many days, centered on the selected outbound date. */
export const GREENFIELD_DATE_STRIP_PAGE_DAYS = 7;

/**
 * @param {{
 *   departDateLabel?: string|null,
 *   returnDateLabel?: string|null,
 *   suggestions?: Array<{ depDate?: string, retDate?: string }>|null,
 * }} input
 * @returns {Array<{ id: string, weekday: string, dateLabel: string, priceLabel: string, selected: boolean, cheapest: boolean, suggestion?: object|null }>}
 */
export function buildGreenfieldDateStrip(input = {}) {
  const selectedDep = normalizeDateLabel(input.departDateLabel);
  const suggestions = Array.isArray(input.suggestions) ? input.suggestions : [];

  /** @type {Map<string, { depDate: string, retDate?: string }>} */
  const byDep = new Map();
  for (const s of suggestions) {
    const dep = normalizeDateLabel(s?.depDate);
    if (!dep) continue;
    byDep.set(dep, {
      depDate: dep,
      retDate: normalizeDateLabel(s?.retDate) || undefined,
    });
  }
  if (selectedDep) {
    byDep.set(selectedDep, {
      depDate: selectedDep,
      retDate: normalizeDateLabel(input.returnDateLabel) || undefined,
    });
  }

  // Seed one visible page around selected when we can parse it
  const center = parseDdMmYyyy(selectedDep);
  const half = (GREENFIELD_DATE_STRIP_PAGE_DAYS - 1) / 2;
  if (center) {
    for (let d = -half; d <= half; d++) {
      const dt = new Date(center);
      dt.setDate(dt.getDate() + d);
      const label = formatDdMmYyyy(dt);
      if (!byDep.has(label)) {
        byDep.set(label, {
          depDate: label,
          retDate: normalizeDateLabel(input.returnDateLabel) || undefined,
        });
      }
    }
  }

  const days = [...byDep.values()]
    .sort((a, b) => parseDdMmYyyy(a.depDate) - parseDdMmYyyy(b.depDate))
    .map((row) => {
      const dt = parseDdMmYyyy(row.depDate);
      const weekday =
        dt && Number.isFinite(dt.getTime())
          ? WEEKDAYS_HE[dt.getDay()]
          : "";
      const short = row.depDate.slice(0, 5);
      return {
        id: `d-${row.depDate}`,
        weekday,
        dateLabel: short,
        // Live: never invent alternate-date prices
        priceLabel: "",
        selected: row.depDate === selectedDep,
        cheapest: false,
        suggestion: row,
      };
    });

  return days;
}

function normalizeDateLabel(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || s.includes("__")) return null;
  const m = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[1]}/${m[2]}/${m[3]}`;
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return null;
}

function parseDdMmYyyy(label) {
  const n = normalizeDateLabel(label);
  if (!n) return null;
  const [dd, mm, yyyy] = n.split("/").map(Number);
  const dt = new Date(yyyy, mm - 1, dd);
  return Number.isFinite(dt.getTime()) ? dt : null;
}

function formatDdMmYyyy(dt) {
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Shift a strip date (DD/MM/YYYY) by a whole number of days. */
export function shiftGreenfieldStripDate(label, deltaDays) {
  const dt = parseDdMmYyyy(label);
  const delta = Number(deltaDays);
  if (!dt || !Number.isFinite(delta) || delta === 0) return null;
  const next = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  next.setDate(next.getDate() + delta);
  return formatDdMmYyyy(next);
}

/** Nights between two DD/MM/YYYY dates. Null when either date is missing or the return is earlier. */
export function flightStayNights(departLabel, returnLabel) {
  const dep = parseDdMmYyyy(departLabel);
  const ret = parseDdMmYyyy(returnLabel);
  if (!dep || !ret) return null;
  const nights = Math.round((ret.getTime() - dep.getTime()) / 86400000);
  if (!Number.isFinite(nights) || nights < 0) return null;
  return nights;
}

/** Hebrew stay line, e.g. "4 לילות · 5 ימים". Null when there is no return date. */
export function formatFlightStayLabel(departLabel, returnLabel) {
  const nights = flightStayNights(departLabel, returnLabel);
  if (nights == null) return null;
  const days = nights + 1;
  const nightsWord = nights === 1 ? "לילה" : "לילות";
  const daysWord = days === 1 ? "יום" : "ימים";
  return `${nights} ${nightsWord} · ${days} ${daysWord}`;
}

/** New return = new departure plus the night count of the current search. */
export function returnDateKeepingNights(newDepartLabel, originalDepartLabel, originalReturnLabel) {
  const nights = flightStayNights(originalDepartLabel, originalReturnLabel);
  const dep = normalizeDateLabel(newDepartLabel);
  if (nights == null || !dep) return null;
  if (nights === 0) return dep;
  return shiftGreenfieldStripDate(dep, nights);
}
